/*
 * Accessibility Adjustments: window.TmAccessibility (panel markup: app/cool-html/sections/accessibility/panel.php,
 * effects: app/assets/css/sections/accessibility.scss). The state lives in localStorage "tm_a11y" (Necessary category,
 * no consent check). Every setting becomes a tm-a11y-* class or a --tm-a11y-* property on <html>; the JS-only effects
 * (font size walk, quick keys, pointer helpers, media, sliders, announcer) live here.
 *
 * Public API: open(), close(), reset(), get(key), set(key, value), setProfile(name, on), on('change', cb).
 *   get/set take camelCase or kebab-case keys ('lineHeight' or 'line-height'); 'profile:<name>' addresses a profile.
 *   change callbacks get {keys: [...], key, value, state} (key/value only when one key changed).
 * Loaded after modal.js in the footer bundle. ES2015 (classes) but no optional chaining, no ES modules.
 */
(function (w, d) {
    'use strict';

    var STORAGE_KEY = 'tm_a11y';
    var MODAL_ID = '#c-a11y-modal';
    var FONT_BASE = '/app/assets/fonts/atkinson/';
    var PREFIX = 'tm-a11y-';

    // ---- Schema: every setting, its default and what a valid value is -----------------------------------------------
    var STEPS = { scale: [-3, 6], fontSize: [-3, 6], lineHeight: [0, 4], letterSpacing: [0, 4] };
    var BOOLS = ['readableFont', 'highlightTitles', 'highlightLinks', 'magnifier', 'mute', 'hideImages', 'readMode',
        'readingGuide', 'stopAnimations', 'readingMask', 'highlightHover', 'highlightFocus'];
    var ENUMS = {
        align: ['left', 'center', 'right'],
        contrast: ['dark', 'light', 'high'],
        saturation: ['high', 'low', 'mono'],
        cursor: ['black', 'white']
    };
    var COLORS = ['textColor', 'titleColor', 'bgColor'];
    var COLOR_PROP = { textColor: 'text', titleColor: 'title', bgColor: 'bg' };
    var PROFILES = ['seizure', 'vision', 'adhd', 'cognitive', 'keyboard', 'screen-reader', 'senior'];

    // profile presets: the controls a profile switches on (keyboard and screen-reader are behaviours, not presets)
    var PRESETS = {
        'seizure': { stopAnimations: true, saturation: 'low' },
        'vision': { fontSize: 2, lineHeight: 1, contrast: 'high', cursor: 'black', highlightLinks: true },
        'adhd': { readingMask: true, stopAnimations: true, mute: true },
        'cognitive': { highlightTitles: true, highlightLinks: true, readableFont: true, readingGuide: true },
        'keyboard': {},
        'screen-reader': {},
        'senior': { fontSize: 1, lineHeight: 1, readableFont: true, highlightLinks: true, cursor: 'black' }
    };

    // words for the screen-reader announcer
    var LABELS = {
        scale: 'Content scaling', fontSize: 'Font size', lineHeight: 'Line height', letterSpacing: 'Letter spacing',
        readableFont: 'Readable font', highlightTitles: 'Highlight titles', highlightLinks: 'Highlight links',
        magnifier: 'Text magnifier', align: 'Alignment', contrast: 'Contrast', saturation: 'Saturation',
        textColor: 'Text color', titleColor: 'Title color', bgColor: 'Background color', mute: 'Mute sounds',
        hideImages: 'Hide images', readMode: 'Read mode', readingGuide: 'Reading guide', stopAnimations: 'Stop animations',
        readingMask: 'Reading mask', highlightHover: 'Highlight hover', highlightFocus: 'Highlight focus', cursor: 'Cursor'
    };
    var PROFILE_LABELS = {
        'seizure': 'Seizure Safety', 'vision': 'Vision Impaired', 'adhd': 'ADHD Friendly', 'cognitive': 'Cognitive and Learning',
        'keyboard': 'Keyboard Navigation', 'screen-reader': 'Screen Reader', 'senior': 'Older Adults'
    };
    var COLOR_NAMES = {
        '#0074c2': 'blue', '#8e44ad': 'purple', '#d0342c': 'red', '#e67e22': 'orange', '#16a085': 'teal',
        '#4c8c2b': 'green', '#ffffff': 'white', '#000000': 'black'
    };

    var LAYOUT_KEYS = ['scale', 'fontSize', 'lineHeight', 'letterSpacing', 'readMode', 'readableFont', 'hideImages'];
    var CUSTOM_PROPS = ['--tm-a11y-line-height', '--tm-a11y-letter-spacing', '--tm-a11y-text', '--tm-a11y-title',
        '--tm-a11y-bg'];
    var TEXT_BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, a, span, td, th, label, button';
    var QUICK_KEYS = {
        m: 'nav a[href], [role="navigation"] a[href], #main-nav a[href]',
        h: 'h1, h2, h3, h4, h5, h6',
        f: 'input:not([type="hidden"]), select, textarea',
        b: 'button, [role="button"]',
        g: 'img'
    };
    var NATIVELY_FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
    var FS_SKIP = '.c-a11y, script, style, i, svg, [class*="fa-"], [id^="tm-a11y-"]';

    // ---- Small helpers ----------------------------------------------------------------------------------------------
    function camel(s) {
        return String(s).replace(/-([a-z])/g, function (m, c) { return c.toUpperCase(); });
    }

    function kebab(s) {
        return String(s).replace(/([A-Z])/g, function (m) { return '-' + m.toLowerCase(); });
    }

    function round2(n) {
        return Math.round(n * 100) / 100;
    }

    function closest(el, selector) {
        while (el && el.nodeType === 1) {
            if (el.matches(selector)) {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    }

    // a direct text child that is not blank
    function hasOwnText(el) {
        for (var n = el.firstChild; n; n = n.nextSibling) {
            if (n.nodeType === 3 && /\S/.test(n.nodeValue)) {
                return true;
            }
        }
        return false;
    }

    function toArray(list) {
        return Array.prototype.slice.call(list || []);
    }

    function defaults() {
        var s = { v: 1, profiles: {}, owned: {} }; // owned: per profile, the controls it changed {key: {from, to}}
        PROFILES.forEach(function (p) { s.profiles[p] = false; });
        Object.keys(STEPS).forEach(function (k) { s[k] = 0; });
        BOOLS.forEach(function (k) { s[k] = false; });
        Object.keys(ENUMS).forEach(function (k) { s[k] = null; });
        COLORS.forEach(function (k) { s[k] = ''; });
        return s;
    }

    // one setting's value checked against the schema; undefined = invalid
    function clean(key, value) {
        if (STEPS[key]) {
            var n = Math.round(Number(value));
            return isFinite(n) ? Math.max(STEPS[key][0], Math.min(STEPS[key][1], n)) : undefined;
        }
        if (BOOLS.indexOf(key) > -1) {
            return value === true || value === 'true' ? true : (value === false || value === 'false' || value == null ? false : undefined);
        }
        if (ENUMS[key]) {
            return value == null || value === '' ? null : (ENUMS[key].indexOf(value) > -1 ? value : undefined);
        }
        if (COLORS.indexOf(key) > -1) {
            return value == null || value === '' ? '' : (/^#[0-9a-f]{6}$/i.test(String(value)) ? String(value).toLowerCase() : undefined);
        }
        return undefined;
    }

    function stepLabel(key, n) {
        if (!n) {
            return 'Default';
        }
        if (key === 'scale' || key === 'fontSize') {
            return (n > 0 ? '+' : '−') + (Math.abs(n) * 10) + '%';
        }
        return '+' + n;
    }

    function loadState() {
        var state = defaults();
        try {
            var raw = w.localStorage.getItem(STORAGE_KEY);
            var data = raw ? JSON.parse(raw) : null;
            if (data && typeof data === 'object') {
                Object.keys(state).forEach(function (key) {
                    if (key === 'v' || key === 'profiles' || !(key in data)) {
                        return;
                    }
                    var value = clean(key, data[key]);
                    if (value !== undefined) {
                        state[key] = value;
                    }
                });
                if (data.profiles && typeof data.profiles === 'object') {
                    PROFILES.forEach(function (p) {
                        if (data.profiles[p] === true) {
                            state.profiles[p] = true;
                        }
                    });
                }
                if (data.owned && typeof data.owned === 'object') {
                    PROFILES.forEach(function (p) {
                        var keys = state.profiles[p] && data.owned[p];
                        if (!keys || typeof keys !== 'object') {
                            return;
                        }
                        Object.keys(PRESETS[p]).forEach(function (key) {
                            var o = keys[key];
                            var from = o && clean(key, o.from);
                            var to = o && clean(key, o.to);
                            if (from !== undefined && to !== undefined && o) {
                                state.owned[p] = state.owned[p] || {};
                                state.owned[p][key] = { from: from, to: to };
                            }
                        });
                    });
                }
            }
        } catch (e) {
            // storage blocked or JSON broken: start from the defaults
        }
        return state;
    }

    // ---- The engine -------------------------------------------------------------------------------------------------
    class TmAccessibility {
        constructor() {
            this.state = loadState();
            this.listeners = [];
            this.root = null;           // the panel element [data-tm-a11y], once init() found it
            this.ready = false;         // init() has run
            this._opener = null;        // the element to give focus back to when the panel closes
            this._isOpen = false;
            this._hasJq = !!(w.jQuery && w.jQuery.fn);
            this._fsActive = false;     // font size inline styles are on the page
            this._fsLevel = 0;          // the font size step they were written for
            this._pending = [];         // nodes added to the page, waiting for the debounced mutation pass
            this._mutTimer = 0;
            this._observer = null;
            this._ptrOn = false;        // pointer listeners attached
            this._lastX = 0;
            this._lastY = null;
            this._magTarget = null;
            this._raf = 0;
            this._keyOn = false;
            this._keyLast = {};
            this._scaleBase = null;     // the brand's --tmFontSizePercentDesktop/Mobile, read before content scaling overrides them
            w.tmA11yReducedMotion = !!this.state.stopAnimations; // before the ready handlers: pageAnimate (tm-document-ready.js) reads it
            this._applyHtml();          // as early as possible: classes and properties on <html> limit the flash
        }

        // ---- Public API ---------------------------------------------------------------------------------------------
        get(key) {
            if (key === 'profiles') {
                return JSON.parse(JSON.stringify(this.state.profiles));
            }
            if (String(key).indexOf('profile:') === 0) {
                return !!this.state.profiles[this._profileKey(String(key).slice(8))];
            }
            return this.state[camel(key)];
        }

        set(key, value) {
            if (String(key).indexOf('profile:') === 0) {
                return this.setProfile(String(key).slice(8), !!value);
            }
            key = camel(key);
            var next = clean(key, value);
            if (next === undefined || next === this.state[key]) {
                return false;
            }
            this.state[key] = next;
            this._after([key], this._message(key, next));
            return true;
        }

        // turn a profile on (sets its preset controls) or off (puts back only the controls it set and nobody changed since).
        // On: a stepper goes up to the preset (max), any other control is set only while it is at its default; what changed is
        // recorded in state.owned[profile] as {from, to}. Off: an owned control still at its "to" value goes to the value
        // another on-profile wants (the highest for a stepper; that profile then owns it), else back to its "from" value.
        setProfile(name, on) {
            var profile = this._profileKey(name);
            on = !!on;
            if (PROFILES.indexOf(profile) < 0 || this.state.profiles[profile] === on) {
                return false;
            }
            var s = this.state;
            var keys = [];
            var preset = PRESETS[profile];
            var def = defaults();
            s.profiles[profile] = on;
            if (on) {
                var owned = {};
                Object.keys(preset).forEach(function (key) {
                    var next = STEPS[key] ? Math.max(s[key], preset[key]) : (s[key] === def[key] ? preset[key] : s[key]);
                    if (next !== s[key]) {
                        owned[key] = { from: s[key], to: next };
                        s[key] = next;
                        keys.push(key);
                    }
                });
                s.owned[profile] = owned;
            } else {
                var mine = s.owned[profile] || {};
                delete s.owned[profile];
                Object.keys(mine).forEach(function (key) {
                    if (s[key] !== mine[key].to) {
                        return; // the visitor changed it since: theirs now
                    }
                    var heir = null;
                    PROFILES.forEach(function (other) {
                        if (other !== profile && s.profiles[other] && PRESETS[other].hasOwnProperty(key)
                            && (heir === null || (STEPS[key] && PRESETS[other][key] > PRESETS[heir][key]))) {
                            heir = other;
                        }
                    });
                    var next = heir ? PRESETS[heir][key] : mine[key].from;
                    if (heir) {
                        s.owned[heir] = s.owned[heir] || {};
                        s.owned[heir][key] = { from: mine[key].from, to: next };
                    }
                    if (next !== s[key]) {
                        s[key] = next;
                        keys.push(key);
                    }
                });
            }
            keys.push('profile:' + profile);
            this._after(keys, PROFILE_LABELS[profile] + ' profile ' + (on ? 'on' : 'off'), profile === 'screen-reader');
            return true;
        }

        reset() {
            var announce = this.state.profiles['screen-reader'];
            try {
                w.localStorage.removeItem(STORAGE_KEY);
            } catch (e) {
                // storage blocked: the in-memory reset still happens
            }
            this.state = defaults();
            this._applyAll();
            this.render();
            this._emit(['reset']);
            this._relayout();
            if (announce) {
                this._announce('Settings reset', true);
            }
        }

        on(event, callback) {
            if (event === 'change' && typeof callback === 'function') {
                this.listeners.push(callback);
            }
            return this;
        }

        open() {
            var modal = this._modal();
            if (!modal) {
                return;
            }
            modal.open();
            if (!this._hasJq) {
                this._onOpened(); // no tmModal_open_ event without jQuery
            }
        }

        close() {
            var modal = this._isOpen ? this._modal() : null; // modal.js close() strips html-modal-open whatever modal is open
            if (modal) {
                modal.close();
                if (!this._hasJq) {
                    this._onClosed();
                }
            }
        }

        // ---- Init: bind the panel, apply the DOM-bound effects ------------------------------------------------------
        init() {
            this.root = d.querySelector('[data-tm-a11y]');
            if (!this.root) {
                return false;
            }
            this.ready = true;
            this._hasJq = !!(w.jQuery && w.jQuery.fn && w.jQuery.fn.on);
            this._bindPanel();
            this._bindOpeners();
            this._bindModalEvents();
            this._applyDom();
            this.render();
            return true;
        }

        // ---- Panel: delegated events --------------------------------------------------------------------------------
        _bindPanel() {
            var self = this;
            this.root.addEventListener('click', function (event) {
                var t = event.target;
                // the whole profile row is the switch's hit area; the switch's own <label> already toggles it (Peter, COOL 533)
                var row = closest(t, '.c-a11y__profile');
                if (row && !closest(t, 'label')) {
                    var sw = row.querySelector('[data-a11y-profile]');
                    if (sw) {
                        sw.click();
                    }
                    return;
                }
                var el = closest(t, '[data-a11y-toggle]');
                if (el && self.root.contains(el)) {
                    event.preventDefault();
                    var tk = camel(el.getAttribute('data-a11y-toggle'));
                    self.set(tk, !self.state[tk]);
                    return;
                }
                el = closest(t, '[data-a11y-radio][data-a11y-value]');
                if (el && self.root.contains(el)) {
                    event.preventDefault();
                    var group = camel(el.getAttribute('data-a11y-radio'));
                    var value = el.getAttribute('data-a11y-value');
                    self.set(group, self.state[group] === value ? null : value);
                    return;
                }
                el = closest(t, '[data-a11y-step]');
                var stepper = el && closest(el, '[data-a11y-stepper]');
                if (el && stepper) {
                    event.preventDefault();
                    var sk = camel(stepper.getAttribute('data-a11y-stepper'));
                    if (STEPS[sk]) {
                        self.set(sk, self.state[sk] + (parseInt(el.getAttribute('data-a11y-step'), 10) || 0));
                    }
                    return;
                }
                el = closest(t, '[data-a11y-color]');
                var swatches = el && closest(el, '[data-a11y-swatches]');
                if (el && swatches) {
                    event.preventDefault();
                    self.set(camel(swatches.getAttribute('data-a11y-swatches')), el.getAttribute('data-a11y-color'));
                    return;
                }
                el = closest(t, '[data-a11y-action]');
                if (el && self.root.contains(el)) {
                    var action = el.getAttribute('data-a11y-action');
                    if (action === 'reset') {
                        event.preventDefault();
                        self.reset();
                    } else if (action === 'close') {
                        event.preventDefault();
                        self.close();
                    } else if (action === 'chat') {
                        event.preventDefault();
                        self.close();
                        if (w.CoolChat && typeof w.CoolChat.open === 'function') {
                            w.CoolChat.open();
                        }
                    }
                }
            });
            this.root.addEventListener('change', function (event) {
                var t = event.target;
                if (t && t.hasAttribute && t.hasAttribute('data-a11y-profile')) {
                    self.setProfile(t.getAttribute('data-a11y-profile'), !!t.checked);
                    self.render(); // a refused change puts the switch back
                } else if (t && t.hasAttribute && t.hasAttribute('data-a11y-links') && t.value) {
                    w.open(t.value, '_blank', 'noopener'); // a new window, like Statement (Peter, COOL 532)
                    t.selectedIndex = 0; // back to "Select an option", so the same page can be picked again
                }
            });
        }

        // footer links and any [data-a11y-open] open the panel (capture: before older handlers on the same link)
        _bindOpeners() {
            var self = this;
            d.addEventListener('click', function (event) {
                var el = closest(event.target, '.accessibility-link, [data-a11y-open]');
                if (el) {
                    event.preventDefault();
                    self._opener = el; // Safari does not focus a link on click: the focus goes back here on close
                    self.open();
                    if (!self._isOpen && self._hasJq) {
                        self._opener = null; // nothing opened (no modal): do not keep it for a later open
                    }
                }
            }, true);
        }

        // ---- Modal: register with modal.js, focus in and back out ---------------------------------------------------
        _modal() {
            var api = typeof Modal !== 'undefined' ? Modal : (w.Modal || null);
            if (!api || !d.querySelector(MODAL_ID)) {
                return null;
            }
            return (api.instance && api.instance(MODAL_ID)) || (api.add && api.add(MODAL_ID)) || null;
        }

        _bindModalEvents() {
            var self = this;
            var el = d.querySelector(MODAL_ID);
            if (this._hasJq) {
                w.jQuery(d).on('tmModal_open_' + MODAL_ID, function () { self._onOpened(); });
                w.jQuery(d).on('tmModal_closed_' + MODAL_ID, function () { self._onClosed(); });
            } else if (el && w.MutationObserver) {
                var wasOpen = el.classList.contains('open');
                new MutationObserver(function () {
                    var isOpen = el.classList.contains('open');
                    if (isOpen !== wasOpen) {
                        wasOpen = isOpen;
                        isOpen ? self._onOpened() : self._onClosed();
                    }
                }).observe(el, { attributes: true, attributeFilter: ['class'] });
            }
        }

        _onOpened() {
            if (this._isOpen) {
                return;
            }
            this._isOpen = true;
            // desktop: the page keeps scrolling beside the popup, as modal.js does for its chat popup (Peter, COOL 533)
            if (w.innerWidth >= 1025) { // breakpoint(desktop) = min-width: 1025px (_variables.scss)
                d.body.classList.remove('body-no-scroll');
                d.documentElement.classList.remove('html-modal-open');
            }
            var active = d.activeElement;
            if (!this._opener) {
                this._opener = active && active !== d.body && !closest(active, '.c-a11y') ? active : null;
            }
            var self = this;
            setTimeout(function () { self._focusFirst(); }, 120); // the drawer is visible once modal.js adds its content class
        }

        _onClosed() {
            if (!this._isOpen) {
                return;
            }
            this._isOpen = false;
            var opener = this._opener;
            this._opener = null;
            if (opener && d.documentElement.contains(opener) && typeof opener.focus === 'function') {
                opener.focus();
            }
        }

        _focusFirst() {
            var panel = d.querySelector('.c-a11y');
            if (!panel) {
                return;
            }
            var found = toArray(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
                .filter(function (el) { return el.getClientRects().length > 0; })[0];
            if (found) {
                try {
                    found.focus({ preventScroll: true });
                } catch (e) {
                    found.focus();
                }
            }
        }

        // ---- Render: the panel mirrors the state --------------------------------------------------------------------
        render() {
            var root = this.root;
            var s = this.state;
            if (!root) {
                return;
            }
            toArray(root.querySelectorAll('[data-a11y-toggle]')).forEach(function (el) {
                var on = !!s[camel(el.getAttribute('data-a11y-toggle'))];
                el.setAttribute('aria-pressed', on ? 'true' : 'false');
                el.classList.toggle('c-btn-selected', on);
            });
            toArray(root.querySelectorAll('[data-a11y-radio][data-a11y-value]')).forEach(function (el) {
                var on = s[camel(el.getAttribute('data-a11y-radio'))] === el.getAttribute('data-a11y-value');
                el.setAttribute('aria-pressed', on ? 'true' : 'false');
                el.classList.toggle('c-btn-selected', on);
            });
            toArray(root.querySelectorAll('[data-a11y-stepper]')).forEach(function (el) {
                var key = camel(el.getAttribute('data-a11y-stepper'));
                var out = el.querySelector('[data-a11y-value]');
                if (out && STEPS[key]) {
                    out.textContent = stepLabel(key, s[key]);
                }
            });
            toArray(root.querySelectorAll('[data-a11y-swatches]')).forEach(function (group) {
                var current = s[camel(group.getAttribute('data-a11y-swatches'))] || '';
                toArray(group.querySelectorAll('.c-a11y__swatch[data-a11y-color]')).forEach(function (el) {
                    var color = (el.getAttribute('data-a11y-color') || '').toLowerCase();
                    var on = !!color && color === current;
                    el.classList.toggle('c-a11y__swatch--on', on);
                    el.setAttribute('aria-pressed', on ? 'true' : 'false');
                });
            });
            toArray(root.querySelectorAll('[data-a11y-profile]')).forEach(function (el) {
                el.checked = !!s.profiles[kebab(el.getAttribute('data-a11y-profile'))];
            });
        }

        // ---- Change pipeline ----------------------------------------------------------------------------------------
        _after(keys, message, forceAnnounce) {
            this._persist();
            this._applyAll();
            this.render();
            this._emit(keys);
            this._announce(message, forceAnnounce); // forced: the Screen Reader profile's own "off"
            if (keys.some(function (k) { return LAYOUT_KEYS.indexOf(k) > -1; })) {
                this._relayout();
            }
        }

        // the page's own layout scripts (sliders, the activity page's sticky booking box) measure on resize: let them
        // re-measure. StickySidebar (main.js) measures its fixed inner wrapper, whose width it wrote itself, so after Read
        // Mode squeezed it the box would stay narrow: its inline geometry is cleared first and the resize writes it again
        _relayout() {
            toArray(d.querySelectorAll('.inner-wrapper-sticky')).forEach(function (el) {
                ['position', 'top', 'left', 'width', 'transform'].forEach(function (p) { el.style.removeProperty(p); });
            });
            w.dispatchEvent(new Event('resize'));
        }

        _persist() {
            try {
                w.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
            } catch (e) {
                // storage blocked: the settings last for this page only
            }
        }

        _emit(keys) {
            var detail = { keys: keys, state: JSON.parse(JSON.stringify(this.state)) };
            if (keys.length === 1 && keys[0].indexOf('profile:') !== 0 && keys[0] !== 'reset') {
                detail.key = keys[0];
                detail.value = this.state[keys[0]];
            }
            this.listeners.slice().forEach(function (cb) {
                try {
                    cb(detail);
                } catch (e) {
                    // one bad listener does not stop the others
                }
            });
        }

        _profileKey(name) {
            return kebab(String(name));
        }

        _message(key, value) {
            var label = LABELS[key] || key;
            if (STEPS[key]) {
                return label + ' ' + (value ? stepLabel(key, value) : 'default');
            }
            if (BOOLS.indexOf(key) > -1) {
                return label + (value ? ' on' : ' off');
            }
            if (COLORS.indexOf(key) > -1) {
                return label + ' ' + (value ? (COLOR_NAMES[value] || value) : 'default');
            }
            return label + ' ' + (value || 'off');
        }

        _isDefault() {
            var def = defaults();
            var s = this.state;
            if (PROFILES.some(function (p) { return s.profiles[p]; })) {
                return false;
            }
            return Object.keys(STEPS).concat(BOOLS, Object.keys(ENUMS), COLORS).every(function (k) { return s[k] === def[k]; });
        }

        _applyAll() {
            this._applyHtml();
            if (this.ready) {
                this._applyDom();
            }
        }

        // ---- Section: classes and custom properties on <html> -------------------------------------------------------
        _applyHtml() {
            var s = this.state;
            var html = d.documentElement;
            var want = {};
            var props = {};
            BOOLS.forEach(function (k) {
                if (s[k]) {
                    want[PREFIX + kebab(k)] = 1;
                }
            });
            Object.keys(ENUMS).forEach(function (group) {
                if (s[group]) {
                    want[PREFIX + group + '-' + s[group]] = 1;
                }
            });
            if (s.lineHeight > 0) {
                want[PREFIX + 'line-height'] = 1;
                props['--tm-a11y-line-height'] = String(round2(1.5 + 0.2 * s.lineHeight));
            }
            if (s.letterSpacing > 0) {
                want[PREFIX + 'letter-spacing'] = 1;
                props['--tm-a11y-letter-spacing'] = round2(0.05 * s.letterSpacing) + 'em';
            }
            COLORS.forEach(function (k) {
                if (s[k]) {
                    want[PREFIX + kebab(k.replace('Color', '')) + '-color'] = 1;
                    props['--tm-a11y-' + COLOR_PROP[k]] = s[k];
                }
            });
            if (s.profiles.keyboard) {
                want[PREFIX + 'keyboard'] = 1;
            }
            if (s.profiles['screen-reader']) {
                want[PREFIX + 'screen-reader'] = 1;
            }
            if (!this._isDefault()) {
                want[PREFIX + 'active'] = 1;
            }
            toArray(html.classList).forEach(function (c) {
                if (c.indexOf(PREFIX) === 0 && !want[c]) {
                    html.classList.remove(c);
                }
            });
            Object.keys(want).forEach(function (c) { html.classList.add(c); });
            CUSTOM_PROPS.forEach(function (p) {
                if (props[p] !== undefined) {
                    html.style.setProperty(p, props[p]);
                } else {
                    html.style.removeProperty(p);
                }
            });
            // content scaling: the html size chain reads these two properties (inline + important beats the site typography);
            // the brand's own percentages (css-files.php) are read once, before the first override, and multiplied
            if (s.scale !== 0) {
                if (!this._scaleBase) {
                    var root = w.getComputedStyle(html);
                    this._scaleBase = {
                        desktop: parseFloat(root.getPropertyValue('--tmFontSizePercentDesktop')) || 1,
                        mobile: parseFloat(root.getPropertyValue('--tmFontSizePercentMobile')) || 1
                    };
                }
                var factor = 1 + 0.1 * s.scale;
                html.style.setProperty('--tmFontSizePercentDesktop', String(round2(this._scaleBase.desktop * factor)), 'important');
                html.style.setProperty('--tmFontSizePercentMobile', String(round2(this._scaleBase.mobile * factor)), 'important');
            } else {
                html.style.removeProperty('--tmFontSizePercentDesktop');
                html.style.removeProperty('--tmFontSizePercentMobile');
            }
            if (s.readableFont) {
                this._injectFonts();
            }
        }

        // ---- Section: Atkinson Hyperlegible, injected once on first use ---------------------------------------------
        _injectFonts() {
            if (d.getElementById('tm-a11y-fonts') || !d.head) {
                return;
            }
            var faces = [['regular', 400, 'normal'], ['italic', 400, 'italic'], ['bold', 700, 'normal'], ['bolditalic', 700, 'italic']];
            var style = d.createElement('style');
            style.id = 'tm-a11y-fonts';
            style.textContent = faces.map(function (f) {
                return '@font-face{font-family:"Atkinson Hyperlegible";font-style:' + f[2] + ';font-weight:' + f[1] +
                    ';font-display:swap;src:url("' + FONT_BASE + 'atkinson-hyperlegible-' + f[0] + '.woff2") format("woff2");}';
            }).join('\n');
            d.head.appendChild(style);
        }

        // ---- Section: everything that needs the body ----------------------------------------------------------------
        _applyDom() {
            var s = this.state;
            this._fxFontSize();
            this._fxScreenReader();
            this._fxKeyboard(!!s.profiles.keyboard);
            this._fxPointer();
            this._fxMedia();
            this._fxAnimations();
            this._fxObserver();
        }

        // ---- Section: font size stepper (inline px on text elements under #main-container) --------------------------
        _fsFactor() {
            return 1 + 0.1 * this.state.fontSize;
        }

        // elements with their own text, not yet marked, among the roots and their descendants (a skipped element's subtree
        // is not entered)
        _fsCollect(roots) {
            var found = [];
            var seen = typeof Set === 'function' ? new Set() : null; // overlapping roots from the observer
            var accept = { acceptNode: function (el) { return el.matches(FS_SKIP) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT; } };
            roots.forEach(function (root) {
                if (root.nodeType !== 1 || closest(root, FS_SKIP)) {
                    return;
                }
                var walker = d.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, accept);
                for (var el = root; el; el = walker.nextNode()) {
                    if (el.hasAttribute('data-a11y-fs') || (seen && seen.has(el))) {
                        continue;
                    }
                    if (hasOwnText(el)) {
                        found.push(el);
                        if (seen) {
                            seen.add(el);
                        }
                    }
                }
            });
            return found;
        }

        // mark new text elements with their original size in rem (px / the html size at capture, so content scaling and the
        // html breakpoints keep applying), then write the scaled size on them: all marked elements, or only the new ones
        _fsApply(roots, onlyNew) {
            var factor = this._fsFactor();
            var rootPx = parseFloat(w.getComputedStyle(d.documentElement).fontSize) || 16;
            var reads = this._fsCollect(roots).map(function (el) {
                var px = parseFloat(w.getComputedStyle(el).fontSize);
                var parent = el.parentElement && closest(el.parentElement, '[data-a11y-fs]');
                // a node added under an already scaled element that merely inherits its size: take the parent's original
                if (parent && px && px === parseFloat(w.getComputedStyle(parent).fontSize)) {
                    return { el: el, size: parseFloat(parent.getAttribute('data-a11y-fs')) };
                }
                return { el: el, size: px / rootPx };
            });
            var fresh = [];
            reads.forEach(function (r) {
                if (r.size > 0) {
                    r.el.setAttribute('data-a11y-fs', String(Math.round(r.size * 10000) / 10000));
                    if (r.el.style.fontSize) {
                        r.el.setAttribute('data-a11y-fs-inline', r.el.style.fontSize);
                    }
                    fresh.push(r.el);
                }
            });
            (onlyNew ? fresh : toArray(d.querySelectorAll('[data-a11y-fs]'))).forEach(function (el) {
                el.style.setProperty('font-size', (Math.round(parseFloat(el.getAttribute('data-a11y-fs')) * factor * 10000) / 10000) + 'rem', 'important');
            });
        }

        _fxFontSize() {
            var container = d.getElementById('main-container');
            if (this.state.fontSize === this._fsLevel) {
                return; // nothing changed since the last pass; late nodes go through the observer
            }
            this._fsLevel = this.state.fontSize;
            if (this.state.fontSize === 0) {
                if (this._fsActive) {
                    toArray(d.querySelectorAll('[data-a11y-fs]')).forEach(function (el) {
                        el.style.removeProperty('font-size');
                        var inline = el.getAttribute('data-a11y-fs-inline');
                        if (inline) {
                            el.style.fontSize = inline;
                        }
                        el.removeAttribute('data-a11y-fs');
                        el.removeAttribute('data-a11y-fs-inline');
                    });
                    this._fsActive = false;
                }
                return;
            }
            if (container) {
                // the first pass walks the page; a later step only rewrites the marked elements (the observer feeds new ones)
                this._fsApply(this._fsActive ? [] : [container], false);
                this._fsActive = true;
            }
        }

        // ---- Section: one debounced MutationObserver for late content (font size, labels, media, sliders) ----------
        _fxObserver() {
            var s = this.state;
            var needed = s.fontSize !== 0 || s.profiles['screen-reader'] || s.mute || s.stopAnimations;
            var target = d.getElementById('main-container');
            if (!needed || !target || !w.MutationObserver) {
                if (this._observer) {
                    this._observer.disconnect();
                    this._observer = null;
                }
                this._pending = [];
                return;
            }
            if (this._observer) {
                return;
            }
            var self = this;
            this._observer = new MutationObserver(function (records) {
                records.forEach(function (record) {
                    // added elements; for added or changed text, its element (a price or counter set after load)
                    var nodes = record.type === 'characterData' ? [record.target] : toArray(record.addedNodes);
                    nodes.forEach(function (node) {
                        var el = node.nodeType === 3 ? node.parentElement : node;
                        if (el && el.nodeType === 1 && self._pending.indexOf(el) < 0 && !closest(el, '.c-a11y, [id^="tm-a11y-"]')) {
                            self._pending.push(el);
                        }
                    });
                });
                if (self._pending.length && !self._mutTimer) {
                    self._mutTimer = setTimeout(function () {
                        self._mutTimer = 0;
                        self._flushPending();
                    }, 250);
                }
            });
            this._observer.observe(target, { childList: true, subtree: true, characterData: true });
        }

        _flushPending() {
            var nodes = this._pending.filter(function (n) { return d.documentElement.contains(n); });
            this._pending = [];
            if (!nodes.length) {
                return;
            }
            var s = this.state;
            if (s.fontSize !== 0) {
                this._fsApply(nodes, true);
            }
            if (s.profiles['screen-reader']) {
                this._srFill(nodes);
            }
            if (s.mute) {
                this._muteIn(nodes);
            }
            if (s.stopAnimations) {
                this._slick(false);
            }
        }

        // ---- Section: screen reader (announcer, aria-label from title) ----------------------------------------------
        _announce(message, force) {
            if (!message || !(force || this.state.profiles['screen-reader'])) {
                return;
            }
            var box = d.getElementById('tm-a11y-announcer');
            if (!box && d.body) {
                box = d.createElement('div');
                box.id = 'tm-a11y-announcer';
                box.setAttribute('aria-live', 'polite');
                box.setAttribute('aria-atomic', 'true');
                box.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
                d.body.appendChild(box);
            }
            if (box) {
                box.textContent = '';
                setTimeout(function () { box.textContent = message; }, 30); // a changed node is what readers announce
            }
        }

        _hasName(el) {
            if ((el.getAttribute('aria-label') || '').trim() || (el.getAttribute('aria-labelledby') || '').trim()) {
                return true;
            }
            if ((el.textContent || '').trim()) {
                return true;
            }
            return toArray(el.querySelectorAll('img[alt]')).some(function (img) { return (img.getAttribute('alt') || '').trim(); });
        }

        _srFill(roots) {
            var self = this;
            roots.forEach(function (root) {
                if (root.nodeType !== 1) {
                    return;
                }
                var list = toArray(root.querySelectorAll('a, button'));
                if (root.matches('a, button')) {
                    list.unshift(root);
                }
                list.forEach(function (el) {
                    var title = (el.getAttribute('title') || '').trim();
                    if (title && !self._hasName(el)) {
                        el.setAttribute('aria-label', title);
                        el.setAttribute('data-a11y-label', '1');
                    }
                });
            });
        }

        _fxScreenReader() {
            if (this.state.profiles['screen-reader']) {
                var container = d.getElementById('main-container');
                if (container) {
                    this._srFill([container]);
                }
            } else {
                toArray(d.querySelectorAll('[data-a11y-label]')).forEach(function (el) {
                    el.removeAttribute('aria-label');
                    el.removeAttribute('data-a11y-label');
                });
            }
        }

        // ---- Section: keyboard profile (skip link, quick keys) ------------------------------------------------------
        _fxKeyboard(on) {
            var skip = d.getElementById('tm-a11y-skip');
            var self = this;
            if (on && d.body) {
                var main = d.getElementById('main-content-area');
                if (main && !main.hasAttribute('tabindex')) {
                    main.setAttribute('tabindex', '-1');
                }
                if (!skip) {
                    skip = d.createElement('a');
                    skip.id = 'tm-a11y-skip';
                    skip.href = '#main-content-area';
                    skip.textContent = 'Skip to content';
                    skip.addEventListener('click', function (event) {
                        var target = d.getElementById('main-content-area');
                        if (target) {
                            event.preventDefault();
                            target.focus();
                            target.scrollIntoView({ block: 'start' });
                        }
                    });
                    d.body.insertBefore(skip, d.body.firstChild);
                }
                if (!this._keyOn) {
                    this._keyOn = true;
                    this._onKey = function (event) { self._quickKey(event); };
                    d.addEventListener('keydown', this._onKey);
                }
            } else {
                if (skip) {
                    skip.parentNode.removeChild(skip);
                }
                if (this._keyOn) {
                    this._keyOn = false;
                    d.removeEventListener('keydown', this._onKey);
                }
            }
        }

        _quickKey(event) {
            var selector = QUICK_KEYS[(event.key || '').toLowerCase()];
            var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
            var target = path[0] || event.target; // the real target inside a shadow root (the Desk chat), not its host
            if (!selector || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented || event.isComposing) {
                return;
            }
            if (target && target.getRootNode && target.getRootNode() !== d) {
                return; // inside a shadow root: typing in a widget
            }
            if (target && target.nodeType === 1 && (target.isContentEditable || closest(target, 'input, textarea, select, [contenteditable]:not([contenteditable="false"])') || closest(target, '.c-a11y'))) {
                return;
            }
            if (this._isOpen || d.documentElement.classList.contains('html-modal-open')) {
                return; // the panel itself (desktop drops html-modal-open, _onOpened) or another modal is open
            }
            var key = event.key.toLowerCase();
            var back = event.shiftKey;
            var list = toArray(d.querySelectorAll(selector)).filter(function (el) {
                return el.getClientRects().length > 0 && !closest(el, '.c-a11y, [id^="tm-a11y-"]');
            });
            if (!list.length) {
                return;
            }
            var ref = d.activeElement && d.activeElement !== d.body ? d.activeElement : this._keyLast[key];
            var next = null;
            var i;
            if (ref && d.documentElement.contains(ref)) {
                if (back) {
                    for (i = list.length - 1; i >= 0 && !next; i--) {
                        var rel = list[i].compareDocumentPosition(ref);
                        if ((rel & Node.DOCUMENT_POSITION_FOLLOWING) && !(rel & Node.DOCUMENT_POSITION_CONTAINS) && list[i] !== ref) {
                            next = list[i];
                        }
                    }
                } else {
                    for (i = 0; i < list.length && !next; i++) {
                        var after = ref.compareDocumentPosition(list[i]);
                        if ((after & Node.DOCUMENT_POSITION_FOLLOWING) && !(after & Node.DOCUMENT_POSITION_CONTAINS) && list[i] !== ref) {
                            next = list[i];
                        }
                    }
                }
            }
            next = next || (back ? list[list.length - 1] : list[0]); // wrap around
            event.preventDefault();
            if (!next.matches(NATIVELY_FOCUSABLE)) {
                next.setAttribute('tabindex', '-1');
            }
            next.focus();
            next.scrollIntoView({ block: 'center', behavior: 'auto' });
            this._keyLast[key] = next;
        }

        // ---- Section: pointer helpers (magnifier, reading guide, reading mask) --------------------------------------
        _el(id, want, setup) {
            var el = d.getElementById(id);
            if (want && !el && d.body) {
                el = d.createElement('div');
                el.id = id;
                el.setAttribute('aria-hidden', 'true');
                setup(el);
                d.body.appendChild(el);
            } else if (!want && el) {
                el.parentNode.removeChild(el);
                el = null;
            }
            return el;
        }

        _fxPointer() {
            var s = this.state;
            var self = this;
            var base = 'position:fixed;pointer-events:none;';
            this._mag = this._el('tm-a11y-magnifier', s.magnifier, function (el) { el.style.cssText = base + 'left:0;top:0;display:none;'; });
            this._guide = this._el('tm-a11y-guide', s.readingGuide, function (el) { el.style.cssText = base + 'left:0;right:0;transform:translateY(-50%);'; });
            this._maskTop = this._el('tm-a11y-mask-top', s.readingMask, function (el) { el.style.cssText = base + 'left:0;right:0;top:0;height:0;'; });
            this._maskBottom = this._el('tm-a11y-mask-bottom', s.readingMask, function (el) { el.style.cssText = base + 'left:0;right:0;bottom:0;'; });
            var any = s.magnifier || s.readingGuide || s.readingMask;
            if (any && !this._ptrOn) {
                this._ptrOn = true;
                this._onMove = function (event) {
                    var point = event.touches && event.touches[0] ? event.touches[0] : event;
                    self._lastX = point.clientX;
                    self._lastY = point.clientY;
                    self._magTarget = event.touches ? null : event.target;
                    if (!self._raf) {
                        self._raf = w.requestAnimationFrame(function () {
                            self._raf = 0;
                            self._paintPointer();
                        });
                    }
                };
                this._onOut = function (event) {
                    if (!event.relatedTarget && self._mag) {
                        self._mag.style.display = 'none';
                    }
                };
                d.addEventListener('mousemove', this._onMove, { passive: true });
                d.addEventListener('touchmove', this._onMove, { passive: true });
                d.addEventListener('mouseout', this._onOut, { passive: true });
            } else if (!any && this._ptrOn) {
                this._ptrOn = false;
                d.removeEventListener('mousemove', this._onMove);
                d.removeEventListener('touchmove', this._onMove);
                d.removeEventListener('mouseout', this._onOut);
            }
            if (this._guide || this._maskTop) {
                if (this._lastY === null) {
                    this._lastY = w.innerHeight / 2;
                }
                this._paintPointer();
            }
        }

        _paintPointer() {
            var y = this._lastY === null ? w.innerHeight / 2 : this._lastY;
            if (this._guide) {
                this._guide.style.top = y + 'px';
            }
            if (this._maskTop && this._maskBottom) {
                this._maskTop.style.height = Math.max(0, y - 60) + 'px';
                this._maskBottom.style.top = (y + 60) + 'px';
            }
            var mag = this._mag;
            if (!mag) {
                return;
            }
            var target = this._magTarget && this._magTarget.nodeType === 1 && !closest(this._magTarget, '.c-a11y, [id^="tm-a11y-"]')
                ? this._magTarget : null;
            // the text block under the pointer, or the element itself when it holds its own text (copy set straight in a div)
            var block = target ? (closest(target, TEXT_BLOCKS) || (hasOwnText(target) ? target : null)) : null;
            var text = block ? String(block.innerText || block.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 300) : '';
            if (!text) {
                mag.style.display = 'none';
                return;
            }
            if (mag.textContent !== text) {
                mag.textContent = text;
            }
            mag.style.display = 'block';
            var left = Math.min(this._lastX + 18, Math.max(8, w.innerWidth - mag.offsetWidth - 8));
            var top = this._lastY + 18;
            if (top + mag.offsetHeight > w.innerHeight - 8) {
                top = Math.max(8, this._lastY - mag.offsetHeight - 18);
            }
            mag.style.left = left + 'px';
            mag.style.top = top + 'px';
        }

        // ---- Section: mute (video/audio) ----------------------------------------------------------------------------
        // only media with sound is touched: an element that is muted already (an autoplay hero video) keeps playing. What
        // is muted here is marked, and what is paused here is marked too, so _fxMedia()'s off path can resume it
        _muteIn(roots) {
            roots.forEach(function (root) {
                if (!root || root.nodeType !== 1) {
                    return;
                }
                var media = toArray(root.querySelectorAll('video, audio'));
                if (root.matches('video, audio')) {
                    media.unshift(root);
                }
                media.forEach(function (el) {
                    if (el.muted || el.hasAttribute('data-a11y-muted')) {
                        return;
                    }
                    el.setAttribute('data-a11y-muted', '1');
                    el.muted = true;
                    if (el.hasAttribute('autoplay')) {
                        el.setAttribute('data-a11y-autoplay', '1');
                        el.removeAttribute('autoplay');
                    }
                    if (!el.paused) {
                        try {
                            el.pause();
                            el.setAttribute('data-a11y-paused', '1');
                        } catch (e) {
                            // a media element that cannot pause is left alone
                        }
                    }
                });
            });
        }

        _fxMedia() {
            if (this.state.mute) {
                this._muteIn([d.documentElement]);
            } else {
                toArray(d.querySelectorAll('[data-a11y-muted], [data-a11y-autoplay], [data-a11y-paused]')).forEach(function (el) {
                    // resumed: what the mute paused, and an autoplay element the mute stopped before it started (a stored
                    // mute applies at DOMContentLoaded, before autoplay; putting the attribute back does not start it)
                    var resume = el.hasAttribute('data-a11y-paused') || el.hasAttribute('data-a11y-autoplay');
                    if (el.hasAttribute('data-a11y-muted')) {
                        el.muted = false;
                        el.removeAttribute('data-a11y-muted');
                    }
                    if (el.hasAttribute('data-a11y-autoplay')) {
                        el.setAttribute('autoplay', '');
                        el.removeAttribute('data-a11y-autoplay');
                    }
                    el.removeAttribute('data-a11y-paused');
                    if (resume) {
                        var playing = el.play(); // the browser may refuse without a gesture
                        if (playing && typeof playing.catch === 'function') {
                            playing.catch(function () {});
                        }
                    }
                });
            }
        }

        // ---- Section: stop animations (sliders, reduced-motion flag) ------------------------------------------------
        _slick(resume) {
            var $ = w.jQuery;
            if (!$ || !$.fn || !$.fn.slick) {
                return;
            }
            $('.slick-initialized').each(function () {
                try {
                    var slider = $(this);
                    if (!resume) {
                        slider.slick('slickPause');
                    } else if (slider.slick('slickGetOption', 'autoplay')) {
                        slider.slick('slickPlay');
                    }
                } catch (e) {
                    // a slider that rejects the call keeps its own behaviour
                }
            });
        }

        _fxAnimations() {
            var on = !!this.state.stopAnimations;
            var was = !!w.tmA11yReducedMotion;
            w.tmA11yReducedMotion = on;
            if (on) {
                this._slick(false);
            } else if (was) {
                this._slick(true);
            }
        }
    }

    // ---- Boot: the singleton exists only where the panel is printed ---------------------------------------------------
    // footer.php prints the panel before the footer scripts, so the check is synchronous: on a page without it (a private
    // page, checkout, a blog without the panel) nothing is applied and there is no window.TmAccessibility
    if (!d.querySelector('[data-tm-a11y]')) {
        return;
    }
    var instance = new TmAccessibility();
    w.TmAccessibility = instance;

    function boot() {
        instance.init();
        w.addEventListener('load', function () {
            // sliders and media that finish loading after DOMContentLoaded
            instance._fxMedia();
            instance._fxAnimations();
        });
    }

    if (d.readyState === 'loading') {
        d.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})(window, document);
